import type { FileRejection } from 'react-dropzone';

/**
 * Largest file we accept before compression. Images above this are rejected
 * with an explicit message rather than being handed to the compressor, which
 * fails opaquely on very large or undecodable files.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const MAX_UPLOAD_LABEL = '25MB';

const formatSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)}MB` : `${Math.ceil(bytes / 1024)}KB`;
};

/**
 * Turns react-dropzone's rejection list into a message that names the actual
 * reason. Without this, a rejected file is dropped silently and the field
 * looks broken.
 */
export const describeRejections = (
    rejections: FileRejection[],
    allowedLabel: string
): string => {
    const [rejection] = rejections;
    if (!rejection) {
        return `That file could not be added. Please upload ${allowedLabel}.`;
    }

    const { file, errors } = rejection;
    const codes = errors.map((error) => error.code);

    if (codes.includes('file-too-large')) {
        return `"${file.name}" is ${formatSize(
            file.size
        )}, which is over the ${MAX_UPLOAD_LABEL} limit. Please upload a smaller file.`;
    }

    if (codes.includes('too-many-files')) {
        return 'Please upload one file at a time.';
    }

    if (codes.includes('file-invalid-type')) {
        const ext = file.name.includes('.')
            ? file.name.split('.').pop()?.toUpperCase()
            : null;
        const which = ext ? `${ext} files are not supported` : 'That file type is not supported';
        return `${which}. Please upload ${allowedLabel}.`;
    }

    return errors[0]?.message || `That file could not be added. Please upload ${allowedLabel}.`;
};

/**
 * HEIC/HEIF is what iPhones produce by default. Most browsers cannot decode it
 * to a canvas, so compression throws with an unhelpful message — name the
 * format explicitly so the user knows what to do instead.
 */
const isUndecodableFormat = (file?: File) => {
    if (!file) return false;
    const name = file.name.toLowerCase();
    return (
        name.endsWith('.heic') ||
        name.endsWith('.heif') ||
        file.type === 'image/heic' ||
        file.type === 'image/heif'
    );
};

/**
 * Maps an upload failure to something the user can act on.
 */
export const uploadErrorMessage = (error: unknown, file?: File): string => {
    if (isUndecodableFormat(file)) {
        return `"${file?.name}" is in HEIC format, which browsers can't process. On iPhone, set Settings > Camera > Formats to "Most Compatible", or save the photo as JPG and try again.`;
    }

    const message = error instanceof Error ? error.message : '';

    if (/decode|canvas|image format|not an image/i.test(message)) {
        return `"${file?.name || 'That file'}" could not be read as an image. Please try a different file or re-save it as a JPG or PNG.`;
    }

    if (/network|failed to fetch|load failed/i.test(message)) {
        return 'The upload could not reach our servers. Check your connection and try again.';
    }

    if (message) {
        return `Upload failed: ${message}`;
    }

    return 'Something went wrong while uploading. Please try again.';
};

/**
 * The upload endpoints need a campaign id. CampaignContext is null for
 * superusers (the v2 form skips fetchMyCampaign for them) and whenever
 * fetchMyCampaign fails, so fall back to the id the rest of the form uses
 * before giving up.
 */
export const resolveCampaignId = (
    campaign: any,
    fallbackCampaignId?: string | null
): string | null =>
    campaign?.campaignDetail?.campaign_id || fallbackCampaignId || null;

export const MISSING_CAMPAIGN_MESSAGE =
    'We could not tell which campaign this fundraiser belongs to, so the upload was not started. Please reload the page and try again.';
